import React from 'react';
import { Button, Text } from '~/components';

import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useAppNavigation from '~/hooks/useAppNavigation';
import { Components } from '../../../components';
import DefaultContainer from '../../components/DefaultContainer';
import { addMonths, format, parseISO } from 'date-fns';
import { useUserInfoStore } from '~/stores/userInfo';
import { Platform, Pressable, View } from 'react-native';
import { formatMoney } from '~/utils';
import PendingInsurance from './components/PendingInsurance';

const HiredInsuranceDetailsAuto: React.FC = () => {
  const { goBack, navigate } = useAppNavigation();
  const contractedServices = useUserInfoStore(
    (state) => state.user?.contractedServices
  );

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_HiredInsuranceDetailsAuto'>
    >();

  const contractedService = contractedServices?.find(
    (service) => service.certificateCode === certificateCode
  );

  if (!contractedService) return null;

  const effectiveDate = format(
    addMonths(parseISO(contractedService.saleDate), contractedService.validity),
    'dd/MM/yyyy'
  );

  return (
    <DefaultContainer type="marisa-auto" className="p-6">
      <Text className="text-base text-gray-600 mb-2">
        Detalhes da contratação
      </Text>

      <View
        className={`rounded-md border border-primary-600 p-4 shadow bg-light-100 mb-6 ${
          Platform.OS === 'android' ? 'shadow-black/40' : ''
        }`}
      >
        <Text className="font-medium text-sm">
          {contractedService.plan?.name}
        </Text>

        <View className="flex-row items-center justify-between mt-4">
          <Text className="text-xs color-gray-400">Valor da Franquia: </Text>
          <Text className="text-xs color-primary-600 font-medium">
            {contractedService.plan?.franchiseValue}
          </Text>
        </View>
      </View>

      <Components.InsuranceStatus data={contractedService} />

      <Components.Divider />

      {contractedService.status === 'approved' ? (
        <>
          <Components.Container>
            <Components.LeftText>Seguradora</Components.LeftText>
            <Components.DateText>
              {contractedService.insuranceCompanyName?.toUpperCase()}
            </Components.DateText>
          </Components.Container>

          <Components.Divider />

          {!!contractedService.holderName && (
            <>
              <Components.Container>
                <Components.LeftText>Nome do Titular</Components.LeftText>
                <Components.RightText>
                  {contractedService.holderName}
                </Components.RightText>
              </Components.Container>

              <Components.Divider />
            </>
          )}

          {!!contractedService.chosenInstallmentValue && (
            <>
              <Components.Container>
                <Components.LeftText>Valor mensal</Components.LeftText>
                <Components.DateText>
                  <Text className="text-sm">R$</Text>
                  {formatMoney(contractedService.chosenInstallmentValue)}
                  <Text className="text-sm">/mês</Text>
                </Components.DateText>
              </Components.Container>

              <Components.Divider />
            </>
          )}

          <Components.Container>
            <Components.LeftText>Vigente até</Components.LeftText>
            <Components.DateText>{effectiveDate}</Components.DateText>
          </Components.Container>

          <Components.Divider />

          <Pressable
            onPress={() =>
              navigate('MarisaServicos_HiredInsuranceCoverageAuto', {
                id: certificateCode,
              })
            }
          >
            <Components.Container>
              <Components.LeftText>Coberturas do seguro</Components.LeftText>
              <Components.Chevron />
            </Components.Container>
          </Pressable>

          <Components.Divider />

          <Pressable
            onPress={() =>
              navigate('MarisaServicos_HiredInsuranceSinisterAuto', {
                id: certificateCode,
              })
            }
          >
            <Components.Container>
              <Components.LeftText>
                Central de Atendimento ao Segurado
              </Components.LeftText>
              <Components.Chevron />
            </Components.Container>
          </Pressable>

          <Components.Divider />

          <Components.InsurancePolicyInfo
            policyNumber={contractedService.policyNumber}
            policyUrl={contractedService.policyUrl}
          />

          <Components.Divider />

          <Pressable
            onPress={() => navigate('MarisaServicos_HiredInsuranceCancelAuto')}
          >
            <Components.Container>
              <Components.LeftText>Cancelamento</Components.LeftText>
              <Components.Chevron />
            </Components.Container>
          </Pressable>

          <Components.Divider />
        </>
      ) : (
        <PendingInsurance />
      )}

      <Button variant="tertiary" onPress={goBack}>
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceDetailsAuto;
